// Thin wrapper around the GA4 gtag snippet installed in public/index.html
// (G-J2F9DP123K, property "mygolfdna.com").
//
// Events are sent ONLY from the production host: stage and local builds ship
// the exact same index.html tag, so without this guard test purchases/signups
// would pollute the production GA property. Everywhere else the event is
// logged to the console instead, which is also how we verify wiring on stage.
//
// gtag can be absent even on prod (ad blockers) — always a safe no-op.
const PROD_HOST = "mygolfdna.com";

export function trackEvent(name, params = {}) {
	// Chrome Prerender2: a URL typed in the address bar may execute this app in
	// a hidden prerendered document BEFORE (or instead of) the visible one.
	// Each prerender is a separate JS realm with a CLONED sessionStorage, so no
	// in-page dedupe can span it — the observable effect on stage was
	// begin_checkout logging 2-3 times per visit. Do what gtag itself does:
	// defer until activation. A discarded prerender never activates, so its
	// events simply never fire.
	if (typeof document !== "undefined" && document.prerendering) {
		document.addEventListener(
			"prerenderingchange",
			() => trackEvent(name, params),
			{ once: true },
		);
		return;
	}

	const host = typeof window !== "undefined" ? window.location.hostname : "";
	const isProdHost = host === PROD_HOST || host === `www.${PROD_HOST}`;

	if (isProdHost && typeof window.gtag === "function") {
		window.gtag("event", name, params);
	} else {
		// eslint-disable-next-line no-console
		console.log("[analytics]", name, params);
	}
}

// Identify the signed-in user to GA4. Without this every report mixes paying
// members, free leads and anonymous visitors, and the same person on phone and
// laptop counts twice — which is why retention/stickiness numbers were
// unreadable (3.2K monthly users vs ~103 paying members).
//
// `userId` MUST be the opaque internal account id — never an email or a name:
// GA4 forbids PII, and the id is enough to join back to our own DB.
// `gtag("set", ...)` applies to every event sent afterwards in this page's
// lifetime, so call it before the first tracked event when possible.
//
// `internal: true` marks our own staff (admins and instructors). It emits the
// `traffic_type=internal` parameter that GA4's built-in internal-traffic data
// filter looks for — the only way to exclude us here, since the office-IP rule
// GA4 offers by default cannot work for a fully remote team. NOTE: the filter
// itself ships in "Testing" mode; until someone flips it to "Active" in
// Admin > Data Settings > Data Filters, the parameter is collected but nothing
// is actually excluded.
export function setAnalyticsUser(userId, { memberType, internal = false } = {}) {
	if (!userId) return;
	const params = { user_id: String(userId) };
	if (internal) params.traffic_type = "internal";
	withGtag((gtag) => {
		gtag("set", params);
		if (memberType) gtag("set", "user_properties", { member_type: memberType });
	}, "set_user", { ...params, member_type: memberType });
}

// Drop the identity on logout so a following anonymous session (or a different
// user on a shared device) is not attributed to the previous account — and,
// just as important, so an admin logging out does not leave `traffic_type`
// stuck on the next person to use the same browser tab.
export function clearAnalyticsUser() {
	withGtag((gtag) => {
		gtag("set", { user_id: null, traffic_type: null });
		gtag("set", "user_properties", { member_type: null });
	}, "clear_user", {});
}

// Shared prod-host guard: run `fn` against gtag on production, otherwise log
// what would have been sent (same contract as trackEvent).
function withGtag(fn, logName, logParams) {
	const host = typeof window !== "undefined" ? window.location.hostname : "";
	const isProdHost = host === PROD_HOST || host === `www.${PROD_HOST}`;
	if (isProdHost && typeof window.gtag === "function") {
		fn(window.gtag);
	} else {
		// eslint-disable-next-line no-console
		console.log("[analytics]", logName, logParams);
	}
}

// Fire an event at most once per 30s for a given key, across DOCUMENTS of the
// same tab, not just within one JS realm. Module state alone proved
// insufficient on stage: navigating to /plans/:id can execute the page in two
// realms (Chrome prerender / duplicate document load), and each realm gets a
// fresh module registry — so the dedupe lives in sessionStorage (shared per
// tab) with an in-memory Set as fallback when storage is unavailable
// (e.g. blocked cookies). The 30s TTL means a genuine later revisit in the
// same tab still counts.
const DEDUPE_TTL_MS = 30000;
const firedOnce = new Set();

export function trackEventOnce(dedupeKey, name, params = {}) {
	const storageKey = `ga4_once:${dedupeKey}`;
	const now = Date.now();
	let last = firedOnce.has(dedupeKey) ? now : 0;
	try {
		last = Math.max(last, Number(sessionStorage.getItem(storageKey) || 0));
	} catch (_) {
		// sessionStorage unavailable — in-memory fallback still dedupes remounts.
	}
	if (now - last < DEDUPE_TTL_MS) return;
	firedOnce.add(dedupeKey);
	try {
		sessionStorage.setItem(storageKey, String(now));
	} catch (_) {}
	trackEvent(name, params);
}
